<template>
	<div class="organization-guest-access-view">
		<template v-if="loading">
			<section class="mb-5">
				<div class="organization-guest-access-view__skeleton-info1 skeleton mb-4" />
				<div class="organization-guest-access-view__skeleton-callout skeleton mb-4" />
			</section>

			<section class="mb-5">
				<div class="flex flex-wrap justify-between py-3">
					<div class="organization-guest-access-view__skeleton-allowed skeleton" />
					<div class="organization-guest-access-view__skeleton-button skeleton" />
				</div>
				<div class="organization-guest-access-view__skeleton skeleton mb-4" />
				<ol class="organization-guest-access-view__skeleton-list w-full list-none">
					<li v-for="skeleton in 5" :key="skeleton" class="organization-guest-access-view__skeleton skeleton my-3" />
				</ol>
			</section>

			<section class="mb-5">
				<div class="organization-guest-access-view__skeleton-consent skeleton mb-4" />
				<div class="organization-guest-access-view__skeleton-info2 skeleton mb-4" />
				<div class="organization-guest-access-view__consent justify-between flex">
					<div class="organization-guest-access-view__skeleton-input skeleton mb-4" />
					<div class="organization-guest-access-view__skeleton-input skeleton mb-4" />
				</div>
			</section>

			<section class="mb-5">
				<div class="organization-guest-access-view__skeleton-welcome skeleton mb-4" />
				<div class="organization-guest-access-view__skeleton-info3 skeleton mb-4" />
				<div class="organization-guest-access-view__skeleton-header skeleton mb-4" />
				<div class="organization-guest-access-view__skeleton-body skeleton" />
			</section>
			<section>
				<div class="organization-guest-access-view__skeleton-consent skeleton mb-4" />
				<ol class="organization-guest-access-view__skeleton-list w-full list-none">
					<li v-for="skeleton in 5" :key="skeleton" class="organization-guest-access-view__skeleton skeleton my-2 py-5" />
				</ol>
			</section>
		</template>

		<template v-else-if="showGuestAccess">
			<section v-if="!model.GuestPortalEnabled || !hasActiveList && model.LinkGuestReferences.length === 0" class="mb-5">
				<CCallout :type="model.GuestPortalEnabled ? 'info' : 'warning'">
					<template v-if="!model.GuestPortalEnabled">
						<p>
							<strong>Your organization has Guest Access turned off.</strong>
							You have <strong>{{ model.UsageByPeriod[0].UsageDetails.length }}</strong>
							{{ model.UsageByPeriod[0].UsageDetails.length === 1 ? 'guest' : 'guests' }} who cannot log in.
							Turn this feature back on so your guests can continue to access their forms and entries in one secure place.
						</p>
					</template>
					<template v-else-if="!hasActiveList">
						<h4 class="mb-2">You can add guests to your organization in two ways:</h4>
						<ol class="pl-4 mb-4">
							<li>Create a guest list to manage guest details like names, emails, photos, and more.</li>
							<li>Send guests authenticated Workflow links or Save & Resume links.</li>
						</ol>
						<a href="/support/504/workflow/guest-access/adding-guests" class="mb-5">Learn how to add and manage guests.</a>
					</template>
				</CCallout>
			</section>

			<section class="mb-5">
				<div class="organization-guest-access-view__allowed-guests bg-primary sticky flex flex-wrap justify-between pt-3 mb-4">
					<span class="inline-flex items-center sz-5">
						<h2>Allowed Guest Types</h2>
						<!-- @vue-ignore target -->
						<CIconButton
							icon="support"
							:intense="true"
							to="/support/501/workflow/guest-access#allowed-guest-types"
							is-external
							target="_blank"
							class="self-center"
						/>
					</span>
					<!-- @vue-ignore data-testid -->
					<CButton
						v-if="!isMobile"
						class="button button--secondary align-self-center"
						data-testid="new-person-form-button"
						@click="showCreatePersonFormDialog = true"
					>
						<template #icon>
							<Inline svg="plus" />
						</template>
						New Person Form
					</CButton>
				</div>
				<p class="mb-4">Guests can always access entries shared with them via public links. Create Person forms to establish guest lists allowing people to submit new entries and manage their guest profile.</p>
				<!-- @vue-ignore data-testid -->
				<CButton
					v-if="isMobile"
					class="button button--secondary mb-4"
					data-testid="new-person-form-button"
					@click="showCreatePersonFormDialog = true"
				>
					<template #icon>
						<Inline svg="plus" />
					</template>
					New Person Form
				</CButton>

				<CGuestAccessList
					:model="model"
					:global-store="globalStore"
					@guest-list-updated="$emit('guest-list-updated', $event)"
					@first-guest-list-enabled="onFirstGuestListEnabled"
					@profile-editing-updated="$emit('profile-editing-updated', $event)"
					@registration-updated="$emit('registration-updated', $event)"
					@show-guest-list-dialog="onShowGuestListDialog"
				/>
			</section>

			<section v-if="model.GuestPortalEnabled" class="mb-5">
				<h2 class="mb-4">Consent</h2>
				<p class="mb-4">Guests must consent to your terms of service and privacy policy, if specified, when they log in the first time.</p>
				<div class="organization-guest-access-view__consent justify-between flex w-100">
					<ValidationObserver ref="tosObserver">
						<!-- @vue-ignore placeholder -->
						<CValidatedInput
							v-model="termsOfService"
							vid="terms-of-service"
							label="Terms of Service"
							name="Terms of Service"
							type="text"
							rules="verify-url"
							placeholder="Link"
							:show-suffix-icon="model?.TermsOfServiceUrl?.length > 0"
							:suffix-link="termsOfService"
							@blur="updateTermsOfService"
						/>
					</ValidationObserver>
					<ValidationObserver ref="policyObserver">
						<!-- @vue-ignore placeholder -->
						<CValidatedInput
							v-model="privacyPolicy"
							vid="privacy-policy"
							label="Privacy Policy"
							name="Privacy Policy"
							type="text"
							rules="verify-url"
							placeholder="Link"
							:show-suffix-icon="model?.PrivacyPolicyUrl?.length > 0"
							:suffix-link="privacyPolicy"
							@blur="updatePrivacyPolicy"
						/>
					</ValidationObserver>
				</div>
			</section>

			<section v-if="model.GuestPortalEnabled" class="mb-5">
				<h2 class="mb-4">Welcome Message</h2>
				<p class="mb-4">Guests will see your welcome message on the home page.</p>
				<div class="organization-guest-access-view__portal-message">
					<!-- @vue-ignore maxlength -->
					<CInput
						id="portal-message-header"
						v-model="internalPortalMessageHeader"
						label="Header"
						type="text"
						autocomplete="off"
						maxlength="140"
						class="organization-guest-access-view__welcome-header mb-4"
						@blur="updatePortalMessage"
					/>
					<label class="label">Body</label>
					<CRichText
						class="c-editor"
						:default-offset="4"
						:is-default-width="false"
						:content="internalPortalMessageBody"
						menu-placement="top-end"
						custom-class="rich-text__menu--portal-message"
						:menu-options="['element', 'emphasize', 'list', 'alignment', 'indentation', 'hr', 'link', 'image']"
						:elements="[
							{ html: 'p', name: 'Paragraph' },
							{ html: 'address', name: 'Address' },
							{ html: 'pre', name: 'Pre' },
							{ html: 'h3', name: 'Heading 3' },
							{ html: 'h4', name: 'Heading 4' },
							{ html: 'h5', name: 'Heading 5' },
							{ html: 'h6', name: 'Heading 6' }
						]"
						is-dialog
						disable-image-upload
						prevent-focus
						@input="internalPortalMessageBody = $event"
						@blur="updatePortalMessage"
					/>
				</div>
			</section>

			<CGuestUsageTable v-if="model.UsageByPeriod?.length > 0" :guest-usage="model.UsageByPeriod" :guest-access-disabled="model.GuestAccessDisabled" />
		</template>

		<template v-else>
			<div class="bg-accent-4 flex items-center gap-4 p-4 flex-col b9:flex-row mb-5">
				<span class="text-center">
					<p>Enable Guest Access to securely collaborate with people outside your organization - including clients, volunteers, members, and more.</p>
				</span>
				<!-- @vue-ignore role -->
				<YouTube role="support-video" class="organization-guest-access-view__upsell-video" v="_dSwJo_WwPA"></YouTube>
			</div>

			<div class="organization-guest-access-view__upsell-features flex flex-col b9:flex-row gap-4 mb-5">
				<div class="flex flex-row b9:flex-col gap-x-4 items-center w-100 b9:w-25">
					<Inline :svg="personSubmissions ? 'complex-icons/guest-access' : 'complex-icons/person-field'" class="mb-3 fill-alert-4" />
					<p class="b9:text-center text-helptext">Give guests a personalized, self-service experience while staying in control of what they see and do.</p>
				</div>
				<div class="flex flex-row b9:flex-col gap-x-4 items-center w-100 b9:w-25">
					<Inline svg="complex-icons/security" class=" mb-3 fill-alert-4" />
					<p class="b9:text-center text-helptext">Allow guests to securely log in with a code sent to their email - no password needed.</p>
				</div>
				<div class="flex flex-row b9:flex-col gap-x-4 items-center w-100 b9:w-25">
					<Inline svg="complex-icons/saved-entry-views" class="mb-3 fill-alert-4" />
					<p class="b9:text-center text-helptext">Let guests view their specific entries, submit forms, and track their progress in one convenient place.</p>
				</div>
				<div class="flex flex-row b9:flex-col gap-x-4 items-center w-100 b9:w-25">
					<Inline svg="complex-icons/email-notifications" class="mb-3 fill-alert-4" />
					<p class="b9:text-center text-helptext">Use automated email reminders to help guests stay on top of assigned tasks and keep your workflows moving forward.</p>
				</div>
			</div>

			<div class="flex flex-col items-center text-center">
				<h3 v-if="globalStore.organization.Plan.Name === 'Team' || globalStore.organization.Plan.Name === 'Enterprise'" class="mb-2">
					Your {{ globalStore.organization.Plan.Name }} plan includes {{ globalStore.organization.Plan.IncludedGuests }} active guests per&nbsp;month.
				</h3>
				<h3 v-else class="mb-2">
					The Team plan includes 5 active guests per&nbsp;month
				</h3>

				<a href="/support/501/workflow/guest-access" class="mb-5">Learn more about guest user pricing</a>

				<button
					v-if="globalStore.organization.Plan.AllowGuests"
					class="button button--primary text-4"
					@click="enableGuestPortal"
				>
					Enable Guest Access
				</button>
				<RouterLink
					v-else
					class="button button--primary no-underline text-4"
					:to="`/${orgCode}/settings/plan/select?source=upgrade-now`"
				>
					Upgrade
				</RouterLink>
			</div>
		</template>

		<CCreatePersonFormContainer
			:show="showCreatePersonFormDialog"
			is-guest-list
			:org-code="orgCode"
			@close="showCreatePersonFormDialog = false"
		/>

		<CGuestListEnabledDialog
			:show="showGuestListDialog && !showGuestAccessWelcome"
			:guest-list="guestListDialogListData"
			@update="$emit('guest-list-updated', $event)"
			@close="showGuestListDialog = false"
		/>

		<CGuestAccessWelcome
			:show="showGuestAccessWelcome"
			@close="showGuestAccessWelcome = false"
		/>
	</div>
</template>
<script lang="ts">
	import type { PropType } from 'vue';
	import { computed, defineComponent, inject, ref, watch } from 'vue';
	import CCallout from 'src/components/content/Callout.vue';
	import CButton from 'src/components/interaction/Button.vue';
	import CIconButton from 'src/components/interaction/IconButton.vue';
	import CInput from 'src/components/form-controls/Input.vue';
	import CValidatedInput from 'src/components/form-controls/ValidatedInput.vue';
	import CRichText from 'src/components/form-controls/RichText/RichText.vue';
	import CGuestUsageTable from './GuestUsageTable.vue';
	import CCreatePersonFormContainer from 'src/components/dialogs/CreatePersonFormContainer.vue';
	import CGuestAccessList, { type IGuestAccessList } from './GuestAccessList.vue';
	import CGuestAccessWelcome from 'src/components/dialogs/guests/GuestAccessWelcome.vue';
	import CGuestListEnabledDialog from 'src/components/dialogs/guests/GuestListEnabledDialog.vue';
	import YouTube from 'src/components/embeds/YouTube.vue';
	import longTextTooltip from 'src/mixins/long-text-tooltip';
	import { extend, ValidationObserver } from 'vee-validate';
	import { isMobileKey } from 'src/utilities/injection-keys';
	import type { GlobalStore } from 'src/stores/global-store';
	import type { GuestAccessViewModel } from '@cognitoforms/types/server-types/core/model/profile/guest-access-view-model';
	import type { UpdatePortalMessageEvent } from './OrganizationGuestAccess.vue';
	import { getResources } from 'src/localization/resource-util';
	import session from '@cognitoforms/api/services/session';

	export default defineComponent({
		components: {
			CCallout,
			CButton,
			CIconButton,
			CInput,
			CValidatedInput,
			ValidationObserver,
			CRichText,
			CGuestUsageTable,
			CCreatePersonFormContainer,
			CGuestAccessList,
			YouTube,
			CGuestAccessWelcome,
			CGuestListEnabledDialog
		},
		mixins: [ longTextTooltip ],
		props: {
			model: { type: Object as PropType<GuestAccessViewModel>, default: null },
			globalStore: {
				type: Object as PropType<GlobalStore>,
				required: true
			}
		},
		emits: [
			'guest-portal-enabled-updated',
			'privacy-policy-updated',
			'tos-updated',
			'guest-list-updated',
			'portal-message-updated',
			'profile-editing-updated',
			'registration-updated'
		],
		setup(props, { emit }) {
			const isMobile = inject(isMobileKey);

			const loading = ref(true);
			const privacyPolicy = ref('');
			const termsOfService = ref('');
			let portalMessageHeader = '';
			let portalMessageBody = '';
			const internalPortalMessageHeader = ref('');
			const internalPortalMessageBody = ref('');
			const sortedGuestList = ref(null);
			const showCreatePersonFormDialog = ref(false);

			const orgCode = computed(() => props.globalStore.organization.Code);
			const personSubmissions = computed(() => session.value?.enabledFeatures?.PersonSubmissions ?? false);

			const showGuestAccess = computed(() => (
				props.model.UsageByPeriod[0].UsageDetails.length > 0 || props.model.GuestPortalEnabled || session.value?.isSupportUser
			));

			watch(() => props.model, async () => {
				if (!props.model)
					return;

				loading.value = false;
				privacyPolicy.value = props.model.PrivacyPolicyUrl ?? '';
				termsOfService.value = props.model.TermsOfServiceUrl ?? '';
				sortedGuestList.value = props.model.GuestLists.filter(gl => !!props.globalStore.forms[gl.Id]).toSorted((a, b) => a.Name.localeCompare(b.Name));

				const resources = await getResources(props.model.PortalLanguageCode);
				portalMessageHeader = props.model.PortalMessageHeader ?? resources.DefaultPortalMessageHeader;
				portalMessageBody = props.model.PortalMessageBody ?? resources.DefaultPortalMessageBody;
				internalPortalMessageHeader.value = portalMessageHeader;
				internalPortalMessageBody.value = portalMessageBody;
			}, { immediate: true });

			extend('verify-url', {
				validate: value => {
					try {
						const newUrl = new URL(value);
						return newUrl.protocol === 'https:';
					}
					catch {
						return false;
					}
				},
				message: 'Please enter a valid URL.'
			});

			const policyObserver = ref<InstanceType<typeof ValidationObserver>>(null);
			const updatePrivacyPolicy = async () => {
				const valid = await policyObserver.value.validate();
				if (!valid)
					return;

				if (privacyPolicy.value === '')
					privacyPolicy.value = null;

				if (privacyPolicy.value === props.model.PrivacyPolicyUrl)
					return;

				emit('privacy-policy-updated', privacyPolicy.value);
			};

			const tosObserver = ref<InstanceType<typeof ValidationObserver>>(null);
			const updateTermsOfService = async () => {
				const valid = await tosObserver.value.validate();
				if (!valid)
					return;

				if (termsOfService.value === '')
					termsOfService.value = null;

				if (termsOfService.value === props.model.TermsOfServiceUrl)
					return;

				emit('tos-updated', termsOfService.value);
			};

			const showGuestAccessWelcome = ref<boolean>(false);
			const showGuestListDialog = ref<boolean>(false);
			const guestListDialogListData = ref<IGuestAccessList>(null);

			const onShowGuestListDialog = (list: IGuestAccessList) => {
				showGuestListDialog.value = true;
				guestListDialogListData.value = list;
			};

			const onFirstGuestListEnabled = () => (showGuestAccessWelcome.value = true);

			const updatePortalMessage = () => {
				if ((portalMessageHeader === internalPortalMessageHeader.value) && (portalMessageBody === internalPortalMessageBody.value))
					return;

				portalMessageHeader = internalPortalMessageHeader.value;
				portalMessageBody = internalPortalMessageBody.value;

				emit('portal-message-updated', {
					header: internalPortalMessageHeader.value,
					body: internalPortalMessageBody.value
				} as UpdatePortalMessageEvent);
			};

			const enableGuestPortal = () => {
				emit('guest-portal-enabled-updated', true);
			};

			const hasActiveList = computed(() => {
				return props.model.GuestLists.some(guestList => guestList.Enabled);
			});

			return {
				privacyPolicy,
				termsOfService,
				policyObserver,
				tosObserver,
				internalPortalMessageHeader,
				internalPortalMessageBody,
				showGuestAccess,
				updatePrivacyPolicy,
				updateTermsOfService,
				updatePortalMessage,
				loading,
				sortedGuestList,
				showCreatePersonFormDialog,
				orgCode,
				isMobile,
				enableGuestPortal,
				hasActiveList,
				showGuestAccessWelcome,
				onFirstGuestListEnabled,
				showGuestListDialog,
				guestListDialogListData,
				onShowGuestListDialog,
				personSubmissions
			};
		}
	});
</script>
<style lang="scss">
.organization-guest-access-view {

	&__consent > *,
	&__welcome-header {
		width: 49%;
	}

	&__portal-message {
		// 5 Lines
		.rich-text__editor--textarea {
			max-height: 163.02px;
		}
		font-size: $size__3;
	}

	&__allowed-guests {
		top: 55px;

		@include b-up(700px) {
			top: 0;
		}
		z-index: $z1;
	}

	&__skeleton {
		height: 19px;

		&-info1 {
			width: 550px;
			height: 19px;
		}

		&-info2 {
			width: 662px;
			height: 19px;
		}

		&-info3 {
			width: 359px;
			height: 19px;
		}

		&-callout {
			height: 176px;
		}

		&-allowed {
			width: 230px;
			height: 35px;
		}

		&-button {
			width: 151px;
			height: 35px;
		}

		&-list {
			mask-image: linear-gradient(to bottom, black 40%, transparent 100%);
		}

		&-consent {
			width: 78px;
			height: 27px;
		}

		&-input {
			height: 52.59px;
		}

		&-welcome {
			width: 176px;
			height: 27px;
		}

		&-header {
			height: 52px;
		}

		&-body {
			height: 70px;
		}

		&-switch {
			width: 42px;
			height: 24px;
			border: 1px;
			border-radius: 120px;
		}
	}

	ul {
		list-style-type: disc;
	}

	&__upsell-video {
		min-width: 250px;

		.play-button {
			width: 20%;
		}

		@include b-up($b5) {
			max-width: 300px;
		}
	}

	&__upsell-features {

		i {
			width: 50px;
			min-width: 50px;
		}
	}
}

@media (max-width: 600px) {

	.rich-text__menu--portal-message {
		max-width: 275px;
	}
}
</style>